import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { fileUrl } from "@/lib/placeholders";
import { useDaCerca } from "@/lib/cerca";
import BottoneRaccogli from "@/components/BottoneRaccogli";
import CampoFirma from "@/components/CampoFirma";
import SceltaMezzo from "@/components/SceltaMezzo";
import {
  ArrowLeft, BookOpen, Check, CheckCircle2, ClipboardCheck, Copy, Download,
  FileText, Loader2, Lock, Minus, PhoneCall, Plus, RotateCcw, Save, Search,
  Smartphone, TableProperties, Trash2, X,
} from "lucide-react";

/**
 * Schede e istruzioni operative.
 *
 * Le procedure rimandano a una quarantina di schede. Qui si vedono tutte, e
 * quelle che si compilano si compilano: il modulo prende tutta la pagina invece
 * di stare in una finestra, perche' su un telefono in cantiere una finestra che
 * scorre dentro una pagina che scorre non si usa. E perche' il campo firma apre
 * gia' una sua finestra a tutto schermo, e due finestre annidate su Radix
 * costano sempre un giro di correzioni.
 */

const DOVE = {
  app:      { testo: "Si compila qui",  colore: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  altrove:  { testo: "Già nell'app",    colore: "bg-sky-50 text-sky-700 border-sky-200" },
  estratta: { testo: "Si estrae",       colore: "bg-slate-100 text-slate-600 border-slate-200" },
};

const ESITI = [
  { codice: "conforme",        breve: "Sì",  icona: Check, classe: "bg-emerald-500 border-emerald-500 text-white" },
  { codice: "non_conforme",    breve: "No",  icona: X,     classe: "bg-red-500 border-red-500 text-white" },
  { codice: "non_applicabile", breve: "N.A.", icona: Minus, classe: "bg-slate-400 border-slate-400 text-white" },
];

function dataIt(iso) {
  if (!iso) return "";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso);
}

// Il browser non puo' scaricare da solo un indirizzo protetto: il file si
// prende con axios, che porta il token, e si consegna come blob.
async function scaricaModulo(id) {
  try {
    const { data, headers } = await api.get(`/sgi/moduli/${id}/pdf`,
                                            { responseType: "blob" });
    const proposto = (headers["content-disposition"] || "").split("filename=")[1];
    const nome = proposto ? proposto.split('"').join("").trim() : "modulo.pdf";
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url; a.download = nome;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Errore nella generazione del PDF");
  }
}

// -----------------------------------------------------------------------------
export default function SchedeOperative({ sistemi, sistemaSel, setSistemaSel, puoScrivere,
                                         daCompilare, onAperta }) {
  const [catalogo, setCatalogo] = useState(null);
  const [carico, setCarico] = useState(true);
  const [cerca, setCerca] = useState("");
  useDaCerca("cerca", setCerca);
  const [soloCantiere, setSoloCantiere] = useState(false);
  const [moduloAperto, setModuloAperto] = useState(null);
  const [schedaEstratta, setSchedaEstratta] = useState(null);
  const [bozze, setBozze] = useState([]);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = sistemaSel ? `?sistema=${sistemaSel}` : "";
      const [c, m] = await Promise.all([
        api.get(`/sgi/schede${p}`),
        api.get("/sgi/moduli?stato=bozza&limite=20"),
      ]);
      setCatalogo(c.data);
      setBozze(m.data);
    } catch {
      toast.error("Errore nel caricamento delle schede");
    } finally { setCarico(false); }
  }, [sistemaSel]);

  useEffect(() => { carica(); }, [carica]);

  const apri = async (scheda, persona) => {
    try {
      // La persona, quando si sa, entra come primo valore: il server la
      // riconosce e tira dentro l'anagrafica dalla scheda del dipendente.
      const corpo = persona && scheda.da_dipendente
        ? { valori: { [scheda.da_dipendente]: persona } }
        : {};
      const { data } = await api.post(`/sgi/schede/${scheda.slug}/moduli`, corpo);
      setModuloAperto(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  // Si arriva qui da un'altra pagina chiedendo una scheda precisa - dal
  // fascicolo del dipendente, o dal documento nel registro. Il modulo si apre
  // da solo: chi e' arrivato fin qui non deve cercarla nell'elenco.
  const chiesta = daCompilare?.slug;
  useEffect(() => {
    if (!chiesta) return;
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get(`/sgi/schede/${chiesta}`);
        if (!vivo) return;
        await apri(data, daCompilare?.persona);
      } catch {
        toast.error("Questa scheda non si compila nell'app");
      } finally { if (onAperta) onAperta(); }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiesta]);

  if (moduloAperto) {
    return (
      <CompilaModulo id={moduloAperto} puoScrivere={puoScrivere}
                     onIndietro={() => { setModuloAperto(null); carica(); }} />
    );
  }

  if (schedaEstratta) {
    return <Estrazione scheda={schedaEstratta} onIndietro={() => setSchedaEstratta(null)} />;
  }

  const filtra = (elenco) => {
    const q = cerca.trim().toLowerCase();
    return elenco.filter((x) => {
      if (soloCantiere && !x.in_cantiere) return false;
      if (!q) return true;
      return `${x.codice} ${x.titolo} ${x.descrizione || ""}`.toLowerCase().includes(q);
    });
  };

  const schede = filtra(catalogo?.schede || []);
  const istruzioni = filtra(catalogo?.istruzioni || []);
  const compilabili = schede.filter((s) => s.dove === "app");
  const rimandi = schede.filter((s) => s.dove !== "app");

  return (
    <div className="space-y-5" data-testid="schede-operative">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
                 placeholder="Cerca una scheda o una istruzione"
                 className="pl-9 h-11" data-testid="cerca-schede" />
        </div>
        <button type="button" onClick={() => setSoloCantiere(!soloCantiere)}
                data-testid="filtro-cantiere"
                className={`inline-flex items-center gap-2 px-4 h-11 rounded-full text-sm font-medium border transition-colors ${
                  soloCantiere ? "bg-[#0067b2] text-white border-[#0067b2]"
                               : "bg-white text-slate-600 border-slate-200"}`}>
          <Smartphone className="w-4 h-4" /> Da compilare sul posto
        </button>
        <select value={sistemaSel} onChange={(e) => setSistemaSel(e.target.value)}
                className="h-11 border border-slate-200 rounded-lg px-3 text-sm bg-white">
          <option value="">Tutte le norme</option>
          {(sistemi || []).map((s) => (
            <option key={s.codice} value={s.codice}>{s.nome}</option>
          ))}
        </select>
      </div>

      {carico && (
        <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
        </p>
      )}

      {!carico && bozze.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-orange-700 mb-2">
              Moduli lasciati a metà
            </div>
            <div className="space-y-1.5">
              {bozze.map((b) => (
                <button key={b.id} type="button" onClick={() => setModuloAperto(b.id)}
                        data-testid={`bozza-${b.id}`}
                        className="w-full text-left flex items-center justify-between gap-3 bg-white border border-orange-200 rounded-lg px-3 py-2.5 hover:border-orange-400">
                  <span className="min-w-0">
                    <span className="block text-sm font-medium truncate">{b.titolo}</span>
                    <span className="block text-xs text-slate-500 truncate">
                      {b.scheda} · {b.scheda_titolo}
                    </span>
                  </span>
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    {b.avanzamento.obbligatori_fatti}/{b.avanzamento.obbligatori} campi
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!carico && (
        <>
          <Sezione titolo="Schede da compilare" quante={compilabili.length}
                   sottotitolo="Si aprono, si riempiono e si chiudono. Chiuse diventano una registrazione e si stampano con il loro codice.">
            <div className="grid gap-3 md:grid-cols-2">
              {compilabili.map((s) => (
                <CartaScheda key={s.codice} scheda={s} onCompila={() => apri(s)}
                             onEstrai={setSchedaEstratta} />
              ))}
            </div>
            {compilabili.length === 0 && (
              <p className="text-sm text-slate-500">Nessuna scheda con questo filtro.</p>
            )}
          </Sezione>

          <Sezione titolo="Schede che il gestionale tiene già" quante={rimandi.length}
                   sottotitolo="Il dato lo raccoglie un'altra pagina: qui è scritto quale, per non compilarlo due volte. Quando l'ente chiede la scheda, «Estrai» la ricostruisce dai dati veri e la stampa con il suo codice.">
            <div className="grid gap-2 md:grid-cols-2">
              {rimandi.map((s) => (
                <CartaRimando key={s.codice} scheda={s} onEstrai={setSchedaEstratta} />
              ))}
            </div>
          </Sezione>

          <Sezione titolo="Istruzioni operative" quante={istruzioni.length}
                   sottotitolo="Non si compilano: si leggono. Sono le sedici istruzioni in vigore, con la revisione corrente.">
            <div className="grid gap-2 md:grid-cols-2">
              {istruzioni.map((i) => <CartaIstruzione key={i.codice} istruzione={i} />)}
            </div>
          </Sezione>
        </>
      )}
    </div>
  );
}

// Le caselle che l'app sa compilare da sola quando si sceglie la persona:
// la chiave e' il codice del campo nella scheda, il valore dice dove pescarlo
// nella scheda del dipendente.
const DAL_DIPENDENTE = {
  nato_a: (e) => e.birth_place,
  nato_il: (e) => (e.birth_date || "").substring(0, 10),
  nazionalita: (e) => e.nationality,
  codice_fiscale: (e) => e.fiscal_code,
  residenza: (e) => [e.residence_address, e.residence_city,
                     e.residence_province ? `(${e.residence_province})` : ""]
    .filter(Boolean).join(" "),
  domicilio: (e) => [e.residence_address, e.residence_city,
                     e.residence_province ? `(${e.residence_province})` : ""]
    .filter(Boolean).join(" "),
  telefono: (e) => e.phone,
  email: (e) => e.email,
};


function Sezione({ titolo, sottotitolo, quante, children }) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="font-display text-xl font-black tracking-tight">
          {titolo} <span className="text-slate-400 font-normal text-base">{quante}</span>
        </h2>
        {sottotitolo && <p className="text-sm text-slate-500">{sottotitolo}</p>}
      </div>
      {children}
    </div>
  );
}

function Tessere({ codici }) {
  return (
    <div className="flex flex-wrap gap-1">
      {(codici || []).slice(0, 4).map((c) => (
        <span key={c} className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
          {c}
        </span>
      ))}
      {(codici || []).length > 4 && (
        <span className="text-[10px] text-slate-400">+{codici.length - 4}</span>
      )}
    </div>
  );
}

// Compilare lo puo' chiunque arrivi a questa pagina, tecnici compresi: e' il
// senso della sezione. Riaprire un modulo chiuso, quello no.
function CartaScheda({ scheda, onCompila, onEstrai }) {
  const [elenco, setElenco] = useState(null);
  const [apro, setApro] = useState(false);

  const vediModuli = async () => {
    if (elenco) { setElenco(null); return; }
    try {
      const { data } = await api.get(`/sgi/schede/${scheda.slug}/moduli`);
      setElenco(data);
    } catch { toast.error("Errore"); }
  };

  const compila = async () => { setApro(true); await onCompila(); setApro(false); };
  const fatti = scheda.moduli.chiusi + scheda.moduli.bozze;

  return (
    <Card className="border-slate-200 hover:border-slate-300 transition-colors">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-mono text-slate-400">{scheda.codice}</div>
            <h3 className="font-semibold leading-tight">{scheda.titolo}</h3>
          </div>
          {scheda.a_turno ? (
            <Badge variant="outline" className="shrink-0 border-sky-200 bg-sky-50 text-sky-700 gap-1">
              <PhoneCall className="w-3 h-3" /> a turno
            </Badge>
          ) : scheda.in_cantiere && (
            <Badge variant="outline" className="shrink-0 border-orange-200 bg-orange-50 text-orange-700 gap-1">
              <Smartphone className="w-3 h-3" /> sul posto
            </Badge>
          )}
        </div>
        <p className="text-sm text-slate-600">{scheda.descrizione}</p>
        <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
          <Tessere codici={scheda.sistemi} />
          <span className="whitespace-nowrap">
            {scheda.quante_voci > 0 && `${scheda.quante_voci} punti · `}
            {scheda.conservazione}
          </span>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Button onClick={compila} disabled={apro}
                  data-testid={`compila-${scheda.slug}`}
                  className="bg-orange-500 hover:bg-orange-600 text-white h-11 flex-1">
            {apro ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <ClipboardCheck className="w-4 h-4 mr-2" />}
            Compila
          </Button>
          {scheda.estraibile && (
            <Button variant="outline" onClick={() => onEstrai(scheda)} className="h-11"
                    data-testid={`estrai-${scheda.slug}`}>
              <TableProperties className="w-4 h-4 mr-2" /> Estrai
            </Button>
          )}
          {fatti > 0 && (
            <Button variant="outline" onClick={vediModuli} className="h-11">
              {fatti} compilat{fatti === 1 ? "o" : "i"}
            </Button>
          )}
        </div>
        {elenco && (
          <div className="space-y-1 border-t border-slate-100 pt-2">
            {elenco.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-2 text-xs py-1">
                <span className="truncate">
                  {m.stato === "chiuso"
                    ? <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-600" />
                    : <span className="inline-block w-2 h-2 rounded-full bg-orange-400 mr-1.5" />}
                  {m.titolo}
                </span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  <button type="button" onClick={() => scaricaModulo(m.id)}
                          className="text-[#0067b2] hover:underline whitespace-nowrap inline-flex items-center gap-1">
                    <Download className="w-3 h-3" /> PDF
                  </button>
                  {/* Il modulo compilato lo scrive l'app quando lo si chiede:
                      da qui si mette da parte come gli altri documenti. */}
                  <BottoneRaccogli className="h-6 w-6 p-0"
                                   voce={{ tipo: "generato", id: `modulo_sgi:${m.id}`,
                                           gruppo: "Sistemi di gestione",
                                           titolo: m.titolo,
                                           sottotitolo: scheda.codice }} />
                </span>
              </div>
            ))}
            {elenco.length === 0 && <p className="text-xs text-slate-400">Nessuno ancora.</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CartaRimando({ scheda, onEstrai }) {
  const d = DOVE[scheda.dove] || DOVE.altrove;
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-mono text-slate-400">{scheda.codice}</div>
          <div className="font-medium text-sm leading-tight">{scheda.titolo}</div>
        </div>
        <span className={`shrink-0 text-[10px] border rounded-full px-2 py-0.5 ${d.colore}`}>
          {d.testo}
        </span>
      </div>
      <p className="text-xs text-slate-500 mt-1.5">{scheda.gia_in}</p>
      {scheda.estraibile && (
        <Button variant="outline" onClick={() => onEstrai(scheda)}
                data-testid={`estrai-${scheda.slug}`}
                className="h-10 mt-2.5 w-full">
          <TableProperties className="w-4 h-4 mr-2" /> Estrai la scheda
        </Button>
      )}
    </div>
  );
}

/**
 * L'estrazione: la scheda ricostruita dai dati che ci sono gia'.
 *
 * L'ente chiede "SC PR-10.2.1" e si aspetta un foglio con quel codice sopra.
 * Il dato pero' e' nella pagina delle non conformita'. Ricopiarlo sarebbe una
 * seconda verita' destinata a divergere dalla prima: qui si estrae, e sopra
 * ci va il codice della scheda.
 */
function Estrazione({ scheda, onIndietro }) {
  const annoOra = new Date().getFullYear();
  const [anno, setAnno] = useState("");
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [scarico, setScarico] = useState(false);

  useEffect(() => {
    let vivo = true;
    setCarico(true);
    api.get(`/sgi/schede/${scheda.slug}/estrazione${anno ? `?anno=${anno}` : ""}`)
      .then(({ data }) => { if (vivo) setDati(data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Errore"))
      .finally(() => { if (vivo) setCarico(false); });
    return () => { vivo = false; };
  }, [scheda.slug, anno]);

  const scarica = async () => {
    setScarico(true);
    try {
      const { data, headers } = await api.get(
        `/sgi/schede/${scheda.slug}/estrazione/pdf${anno ? `?anno=${anno}` : ""}`,
        { responseType: "blob" });
      const proposto = (headers["content-disposition"] || "").split("filename=")[1];
      const nome = proposto ? proposto.split('"').join("").trim() : "estrazione.pdf";
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = nome;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nella generazione del PDF");
    } finally { setScarico(false); }
  };

  const anni = [annoOra, annoOra - 1, annoOra - 2, annoOra - 3];

  return (
    <div className="space-y-4" data-testid="estrazione">
      <div>
        <button type="button" onClick={onIndietro}
                className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-1">
          <ArrowLeft className="w-3 h-3" /> Tutte le schede
        </button>
        <div className="text-[11px] font-mono text-slate-400">{scheda.codice}</div>
        <h2 className="font-display text-2xl font-black tracking-tight leading-tight">
          {scheda.titolo}
        </h2>
        <p className="text-sm text-slate-500 mt-1">{scheda.gia_in}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select value={anno} onChange={(e) => setAnno(e.target.value)}
                data-testid="anno-estrazione"
                className="h-11 border border-slate-200 rounded-lg px-3 text-sm bg-white">
          <option value="">Tutti gli anni</option>
          {anni.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Button onClick={scarica} disabled={scarico || carico}
                className="bg-orange-500 hover:bg-orange-600 text-white h-11">
          {scarico ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                   : <Download className="w-4 h-4 mr-2" />}
          Scarica in PDF
        </Button>
        {dati && (
          <span className="text-sm text-slate-500">
            {dati.quante} {dati.quante === 1 ? "riga" : "righe"}
          </span>
        )}
      </div>

      {dati?.nota && <p className="text-sm text-slate-500">{dati.nota}</p>}

      {carico && (
        <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
        </p>
      )}

      {!carico && dati && (
        dati.righe.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
            Nessun dato registrato per questo periodo.
          </p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0067b2] text-white text-left">
                  {dati.colonne.map((c) => (
                    <th key={c} className="px-3 py-2 font-medium whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dati.righe.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    {r.map((x, j) => (
                      <td key={j} className="px-3 py-2 align-top border-t border-slate-100">
                        {x}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function CartaIstruzione({ istruzione }) {
  const doc = istruzione.documento;
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white flex items-start gap-3">
      <BookOpen className="w-4 h-4 text-[#0067b2] mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-mono text-slate-400">{istruzione.codice}</div>
        <div className="font-medium text-sm leading-tight">{istruzione.titolo}</div>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <Tessere codici={istruzione.sistemi} />
          {doc && doc.file_url ? (
            <a href={fileUrl(doc.file_url)} target="_blank" rel="noopener noreferrer"
               className="text-xs text-[#0067b2] hover:underline whitespace-nowrap inline-flex items-center gap-1">
              <FileText className="w-3 h-3" /> rev. {doc.revisione}
            </a>
          ) : doc ? (
            <span className="text-xs text-slate-500 whitespace-nowrap">rev. {doc.revisione}</span>
          ) : (
            <span className="text-xs text-slate-400 whitespace-nowrap">non ancora caricata</span>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Il modulo aperto: prende tutta la pagina
// -----------------------------------------------------------------------------
function CompilaModulo({ id, puoScrivere, onIndietro }) {
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [salvo, setSalvo] = useState(false);
  const [sporco, setSporco] = useState(false);
  const [persone, setPersone] = useState([]);
  const [mezzi, setMezzi] = useState([]);

  const [valori, setValori] = useState({});
  const [voci, setVoci] = useState({});
  const [righe, setRighe] = useState([]);
  const [firme, setFirme] = useState([]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get(`/sgi/moduli/${id}`);
        if (!vivo) return;
        setDati(data);
        setValori(data.modulo.valori || {});
        setVoci(data.modulo.voci || {});
        setRighe(data.modulo.righe || []);
        const gia = data.modulo.firme || [];
        setFirme((data.scheda.firme || []).map((ruolo) => {
          const trovata = gia.find((f) => f.ruolo === ruolo);
          return trovata || { ruolo, nome: "", immagine: null };
        }));
      } catch {
        toast.error("Modulo non trovato");
        onIndietro();
      } finally { if (vivo) setCarico(false); }
    })();
    return () => { vivo = false; };
  }, [id, onIndietro]);

  const scheda = dati?.scheda;
  const chiuso = dati?.modulo?.stato === "chiuso";

  const serve = useMemo(() => {
    if (!scheda) return { persone: false, mezzi: false };
    const tutti = [...(scheda.campi || []), ...(scheda.campi_finali || []),
                   ...((scheda.righe || {}).campi || [])];
    return {
      persone: tutti.some((c) => c.tipo === "persona"),
      mezzi: tutti.some((c) => c.tipo === "mezzo"),
    };
  }, [scheda]);

  useEffect(() => {
    if (serve.persone) api.get("/employees").then(({ data }) => setPersone(data)).catch(() => {});
    if (serve.mezzi) api.get("/equipment").then(({ data }) => setMezzi(data)).catch(() => {});
  }, [serve.persone, serve.mezzi]);

  const corpo = () => ({ valori, voci, righe, firme });

  const salva = async (silenzioso) => {
    setSalvo(true);
    try {
      const { data } = await api.put(`/sgi/moduli/${id}`, corpo());
      setDati((p) => ({ ...p, modulo: data.modulo, avanzamento: data.avanzamento,
                        mancanze: data.mancanze }));
      setSporco(false);
      if (!silenzioso) toast.success("Salvato");
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
      return null;
    } finally { setSalvo(false); }
  };

  const chiudi = async () => {
    const salvato = await salva(true);
    if (!salvato) return;
    if (salvato.mancanze.length) {
      toast.error(`Manca ancora: ${salvato.mancanze.join(", ")}`);
      return;
    }
    try {
      await api.post(`/sgi/moduli/${id}/chiudi`);
      toast.success("Modulo chiuso: ora è una registrazione");
      onIndietro();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const riapri = async () => {
    try {
      await api.post(`/sgi/moduli/${id}/riapri`);
      const { data } = await api.get(`/sgi/moduli/${id}`);
      setDati(data);
      toast.success("Modulo riaperto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const elimina = async () => {
    const avviso = chiuso
      ? "Questo modulo è chiuso: è una registrazione del sistema.\n\n"
        + "Eliminarla la toglie anche dalle estrazioni e dai conteggi. Procedere?"
      : "Eliminare questo modulo?";
    if (!window.confirm(avviso)) return;
    try {
      await api.delete(`/sgi/moduli/${id}`);
      toast.success("Modulo eliminato");
      onIndietro();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const indietro = async () => {
    if (sporco && !chiuso) await salva(true);
    onIndietro();
  };

  if (carico || !scheda) {
    return (
      <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
      </p>
    );
  }

  const cambiaValore = (codice, v) => {
    setValori((p) => {
      const nuovi = { ...p, [codice]: v };
      // Se la scheda dichiara `da_dipendente`, scegliere la persona riempie
      // l'anagrafica: luogo e data di nascita, codice fiscale, residenza,
      // recapiti. Sono dati che l'app ha gia' nella scheda del dipendente, e
      // ricopiarli a mano vuol dire solo sbagliarli. Si scrivono solo nelle
      // caselle vuote: una correzione fatta a mano resta.
      if (scheda?.da_dipendente === codice) {
        const chi = persone.find((x) => x.id === v);
        if (chi) {
          for (const [casella, prendi] of Object.entries(DAL_DIPENDENTE)) {
            const gia = nuovi[casella];
            if (gia === undefined || gia === null || gia === "") {
              const preso = prendi(chi);
              if (preso) nuovi[casella] = preso;
            }
          }
        }
      }
      return nuovi;
    });
    setSporco(true);
  };
  const cambiaVoce = (codice, esito) => {
    setVoci((p) => ({ ...p, [codice]: { ...(p[codice] || {}), esito } }));
    setSporco(true);
  };
  const cambiaNota = (codice, nota) => {
    setVoci((p) => ({ ...p, [codice]: { ...(p[codice] || {}), nota } }));
    setSporco(true);
  };
  const cambiaRiga = (i, codice, v) => {
    setRighe((p) => p.map((r, j) => (j === i ? { ...r, [codice]: v } : r)));
    setSporco(true);
  };
  const cambiaFirma = (i, campo, v) => {
    setFirme((p) => p.map((f, j) => (j === i ? { ...f, [campo]: v } : f)));
    setSporco(true);
  };

  const av = dati.avanzamento || {};

  return (
    <div className="space-y-4" data-testid="compila-modulo">
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 -mx-4 px-4 py-3 md:mx-0 md:px-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <button type="button" onClick={indietro}
                    className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-1">
              <ArrowLeft className="w-3 h-3" /> Tutte le schede
            </button>
            <div className="text-[11px] font-mono text-slate-400">{scheda.codice}</div>
            <h2 className="font-display text-2xl font-black tracking-tight leading-tight">
              {scheda.titolo}
            </h2>
          </div>
          {chiuso ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1 shrink-0">
              <Lock className="w-3 h-3" /> chiuso
            </Badge>
          ) : (
            <span className="text-xs text-slate-500 whitespace-nowrap shrink-0">
              {av.obbligatori_fatti}/{av.obbligatori} obbligatori
              {av.voci > 0 && ` · ${av.voci_fatte}/${av.voci} punti`}
            </span>
          )}
        </div>
        {!chiuso && (
          <div className="flex items-center gap-2 mt-3">
            <Button onClick={() => salva(false)} disabled={salvo} variant="outline" className="h-11">
              {salvo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva
            </Button>
            <Button onClick={chiudi} disabled={salvo}
                    data-testid="chiudi-modulo"
                    className="bg-orange-500 hover:bg-orange-600 text-white h-11 flex-1">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Chiudi il modulo
            </Button>
          </div>
        )}
        {chiuso && (
          <div className="flex items-center gap-2 mt-3">
            <Button onClick={() => scaricaModulo(id)}
                    className="bg-[#0067b2] hover:bg-[#00589b] text-white h-11 flex-1">
              <Download className="w-4 h-4 mr-2" /> Scarica il PDF
            </Button>
            {puoScrivere && (
              <Button onClick={riapri} variant="outline" className="h-11">
                <RotateCcw className="w-4 h-4 mr-2" /> Riapri
              </Button>
            )}
          </div>
        )}
      </div>

      {chiuso && (
        <p className="text-sm text-slate-500">
          Chiuso da {dati.modulo.chiuso_da} il {dataIt(dati.modulo.chiuso_il)}. Una
          registrazione chiusa non si modifica per sbaglio: per correggerla usa
          «Riapri», e in fondo alla pagina puoi eliminarla.
        </p>
      )}

      {scheda.telefono && !chiuso && (
        <PromemoriaTelefono telefono={scheda.telefono} valori={valori}
                            persone={persone} />
      )}

      {scheda.a_turno && !chiuso && (
        <Coda slug={scheda.slug} sceltoId={valori.autista}
              onScegli={(x) => {
                setValori((p) => ({
                  ...p, autista: x.id, numero_patente: x.numero_patente || "",
                  data_nascita: x.data_nascita || "",
                  categorie: x.categorie || "",
                  scadenza_patente: x.scadenza_patente || "",
                }));
                setSporco(true);
              }} />
      )}

      {!chiuso && dati.mancanze?.length > 0 && (
        <div className="text-sm border border-orange-200 bg-orange-50 rounded-lg px-3 py-2 text-orange-800">
          Per chiudere manca: {dati.mancanze.join(", ")}.
        </div>
      )}

      <BloccoCampi campi={scheda.campi} valori={valori} onCambia={cambiaValore}
                   persone={persone} mezzi={mezzi} bloccato={chiuso} />

      {(scheda.voci || []).length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-1">
            <h3 className="font-semibold mb-2">Lista di riscontro</h3>
            {scheda.voci.map((v) => {
              const stato = voci[v.codice] || {};
              return (
                <div key={v.codice} className="py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-sm flex-1">{v.nome}</span>
                    <div className="flex gap-1 shrink-0">
                      {ESITI.map((e) => {
                        const Icona = e.icona;
                        const scelto = stato.esito === e.codice;
                        return (
                          <button key={e.codice} type="button" disabled={chiuso}
                                  onClick={() => cambiaVoce(v.codice, scelto ? null : e.codice)}
                                  data-testid={`voce-${v.codice}-${e.codice}`}
                                  title={e.breve}
                                  className={`w-11 h-11 rounded-lg border flex items-center justify-center transition-colors ${
                                    scelto ? e.classe
                                           : "bg-white border-slate-200 text-slate-400 hover:border-slate-400"}`}>
                            <Icona className="w-4 h-4" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {(stato.esito === "non_conforme" || stato.nota) && (
                    <Input value={stato.nota || ""} disabled={chiuso}
                           onChange={(e) => cambiaNota(v.codice, e.target.value)}
                           placeholder="Che cosa non va" className="mt-2 h-10" />
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {scheda.righe && (
        <BloccoRighe definizione={scheda.righe} righe={righe} bloccato={chiuso}
                     persone={persone} mezzi={mezzi}
                     onCambia={cambiaRiga}
                     onAggiungi={() => { setRighe((p) => [...p, {}]); setSporco(true); }}
                     onTogli={(i) => { setRighe((p) => p.filter((r, j) => j !== i)); setSporco(true); }} />
      )}

      {(scheda.campi_finali || []).length > 0 && (
        <BloccoCampi titolo="Esito" campi={scheda.campi_finali} valori={valori}
                     onCambia={cambiaValore} persone={persone} mezzi={mezzi}
                     bloccato={chiuso} />
      )}

      {firme.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div>
              <h3 className="font-semibold">Firme</h3>
              <p className="text-xs text-slate-500">
                Si firma col dito. Le firme non impediscono di chiudere il modulo: se
                manca qualcuno si chiude lo stesso e si aggiunge dopo, riaprendolo.
              </p>
            </div>
            {firme.map((f, i) => (
              <div key={f.ruolo} className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">{f.ruolo}</Label>
                <Input value={f.nome || ""} disabled={chiuso}
                       onChange={(e) => cambiaFirma(i, "nome", e.target.value)}
                       placeholder="Nome e cognome" className="h-11" />
                {chiuso ? (
                  f.immagine
                    ? <img src={f.immagine} alt={f.ruolo}
                           className="w-full h-24 object-contain border border-slate-200 rounded bg-white" />
                    : <p className="text-xs text-slate-400">Non firmato.</p>
                ) : (
                  <CampoFirma valore={f.immagine} etichetta="Firma"
                              onChange={(v) => cambiaFirma(i, "immagine", v)} />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between gap-2 pb-8">
        <p className="text-xs text-slate-400">
          Procedura {scheda.procedura} · la tiene {scheda.chi} · si conserva {scheda.conservazione}
        </p>
        {puoScrivere && (
          <Button variant="ghost" onClick={elimina}
                  data-testid="elimina-modulo"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9">
            <Trash2 className="w-4 h-4 mr-2" /> Elimina
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Il promemoria col numero da chiamare.
 *
 * Sta in cima al modulo perche' e' la prima cosa che serve, e il numero e' un
 * link tel: cosi' dal telefono si tocca e parte la chiamata invece di
 * ricopiarlo. Sotto ci sono i due dati che il servizio chiede, con il pulsante
 * per copiarli: leggerli da qui mentre si e' al telefono e' tutto il punto.
 */
function PromemoriaTelefono({ telefono, valori, persone }) {
  const [copiato, setCopiato] = useState(null);
  const copia = async (etichetta, testo) => {
    try {
      await navigator.clipboard.writeText(testo);
      setCopiato(etichetta);
      setTimeout(() => setCopiato(null), 1500);
    } catch {
      toast.error("Il telefono non ha lasciato copiare: leggilo da qui");
    }
  };
  const autista = persone.find((p) => p.id === valori.autista);
  const dati = [
    ["Numero della patente", valori.numero_patente],
    ["Data di nascita", dataIt(valori.data_nascita)],
  ].filter(([, v]) => v);

  return (
    <Card className="border-2 border-orange-300 bg-orange-50/60">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <PhoneCall className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-widest text-orange-700">
              {telefono.nome}
            </div>
            <a href={`tel:${telefono.numero}`}
               data-testid="chiama-servizio"
               className="font-display text-3xl font-black tracking-tight text-[#0067b2] hover:underline block leading-tight">
              {telefono.numero.replace(/^(\d{2})(\d+)$/, "$1 $2")}
            </a>
          </div>
          <Button type="button" variant="outline"
                  onClick={() => copia("numero", telefono.numero)}
                  className="h-10 shrink-0 bg-white">
            {copiato === "numero" ? <Check className="w-4 h-4" />
                                  : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-sm text-orange-900">{telefono.istruzioni}</p>
        {autista && (
          <p className="text-sm font-medium">Autista di turno: {autista.full_name}</p>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          {dati.map(([etichetta, valore]) => (
            <button key={etichetta} type="button" onClick={() => copia(etichetta, valore)}
                    className="text-left bg-white border border-orange-200 rounded-lg px-3 py-2 hover:border-orange-400">
              <span className="block text-[11px] uppercase tracking-wide text-slate-500">
                {etichetta}
              </span>
              <span className="flex items-center justify-between gap-2">
                <span className="font-mono text-lg tracking-wide">{valore}</span>
                {copiato === etichetta ? <Check className="w-4 h-4 text-emerald-600" />
                                       : <Copy className="w-4 h-4 text-slate-400" />}
              </span>
            </button>
          ))}
        </div>
        {dati.length < 2 && (
          <p className="text-sm text-orange-900">
            Manca qualcosa fra numero di patente e data di nascita: scrivilo nei campi
            qui sotto. Il numero di patente viene salvato sul documento della persona,
            così la prossima volta è già qui.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * La coda del giro: chi tocca adesso e chi sta dietro.
 *
 * A caso, ma a turno: l'app propone uno fra quelli che aspettano da piu'
 * tempo. Se quel giorno non e' raggiungibile si sceglie un altro dalla coda,
 * e chi e' stato saltato resta comunque in cima al prossimo giro.
 */
function Coda({ slug, sceltoId, onScegli }) {
  const [giro, setGiro] = useState(null);
  const [aperta, setAperta] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get(`/sgi/schede/${slug}/turno`)
      .then(({ data }) => { if (vivo) setGiro(data); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [slug]);

  if (!giro || giro.coda.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        Nessun autista in archivio: il giro parte da chi ha una patente fra i
        documenti personali.
      </p>
    );
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <button type="button" onClick={() => setAperta(!aperta)}
                data-testid="apri-coda"
                className="w-full flex items-center justify-between gap-2 text-left">
          <span>
            <span className="font-semibold block">
              Il giro dei controlli · {giro.fatti_nel_quadrimestre} su{" "}
              {giro.obiettivo_quadrimestre} in questo quadrimestre
            </span>
            <span className="text-xs text-slate-500">
              {giro.coda.length} autisti, {giro.con_patente_c} con la patente C ·{" "}
              {giro.mai_controllati} mai controllati
            </span>
          </span>
          <span className="text-xs text-[#0067b2]">
            {aperta ? "chiudi" : "vedi la coda"}
          </span>
        </button>
        {aperta && (
          <div className="space-y-1">
            {giro.coda.map((x) => (
              <button key={x.id} type="button" onClick={() => onScegli(x)}
                      className={`w-full text-left flex items-center justify-between gap-3 border rounded-lg px-3 py-2 ${
                        x.id === sceltoId ? "border-orange-400 bg-orange-50"
                                          : "border-slate-200 hover:border-slate-300"}`}>
                <span className="min-w-0">
                  <span className="block text-sm font-medium truncate">
                    {x.nome}
                    {x.patente_c && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide bg-sky-100 text-sky-700 rounded px-1.5 py-0.5">
                        patente C
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {x.numero_patente || "numero da recuperare"}
                  </span>
                </span>
                <span className="text-xs text-slate-500 whitespace-nowrap">
                  {x.ultimo_controllo
                    ? `${dataIt(x.ultimo_controllo)}${x.ultimi_punti != null ? ` · ${x.ultimi_punti} punti` : ""}`
                    : "mai controllato"}
                </span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BloccoCampi({ titolo, campi, valori, onCambia, persone, mezzi, bloccato }) {
  if (!campi || campi.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        {titolo && <h3 className="font-semibold">{titolo}</h3>}
        <div className="grid gap-4 md:grid-cols-2">
          {campi.map((c) => (
            <div key={c.codice} className={c.tipo === "testo_lungo" ? "md:col-span-2" : ""}>
              {c.tipo !== "si_no" && (
                <Label className="text-xs uppercase tracking-wide text-slate-500">
                  {c.nome}{c.obbligatorio && <span className="text-orange-500"> *</span>}
                </Label>
              )}
              <div className={c.tipo === "si_no" ? "" : "mt-1"}>
                <Campo campo={c} valore={valori[c.codice]} bloccato={bloccato}
                       onCambia={(v) => onCambia(c.codice, v)}
                       persone={persone} mezzi={mezzi} />
              </div>
              {c.nota && <p className="text-xs text-slate-400 mt-1">{c.nota}</p>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function BloccoRighe({ definizione, righe, onCambia, onAggiungi, onTogli, persone, mezzi, bloccato }) {
  const campi = definizione.campi || [];
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{definizione.nome}</h3>
          {!bloccato && (
            <Button variant="outline" onClick={onAggiungi} className="h-10"
                    data-testid="aggiungi-riga">
              <Plus className="w-4 h-4 mr-2" /> Aggiungi
            </Button>
          )}
        </div>
        {righe.length === 0 && (
          <p className="text-sm text-slate-400">Nessuna riga. Le righe vuote non si stampano.</p>
        )}
        {righe.map((r, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-400">Riga {i + 1}</span>
              {!bloccato && (
                <button type="button" onClick={() => onTogli(i)}
                        className="text-slate-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {campi.map((c) => (
                <div key={c.codice} className={c.tipo === "testo_lungo" ? "md:col-span-2" : ""}>
                  {c.tipo !== "si_no" && (
                    <Label className="text-xs text-slate-500">{c.nome}</Label>
                  )}
                  <div className={c.tipo === "si_no" ? "" : "mt-1"}>
                    <Campo campo={c} valore={r[c.codice]} bloccato={bloccato}
                           onCambia={(v) => onCambia(i, c.codice, v)}
                           persone={persone} mezzi={mezzi} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Campo({ campo, valore, onCambia, persone, mezzi, bloccato }) {
  const classe = "w-full h-11 border border-slate-200 rounded-lg px-3 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500";

  if (campo.tipo === "testo_lungo") {
    return (
      <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[80px] disabled:bg-slate-50"
                disabled={bloccato} value={valore || ""}
                onChange={(e) => onCambia(e.target.value)} />
    );
  }
  if (campo.tipo === "si_no") {
    return (
      <label className="flex items-center gap-3 text-sm py-2 cursor-pointer">
        <input type="checkbox" disabled={bloccato} checked={Boolean(valore)}
               onChange={(e) => onCambia(e.target.checked)}
               className="w-5 h-5 accent-[#0067b2]" />
        <span>{campo.nome}</span>
      </label>
    );
  }
  if (campo.tipo === "data") {
    return <input type="date" className={classe} disabled={bloccato}
                  value={valore || ""} onChange={(e) => onCambia(e.target.value)} />;
  }
  if (campo.tipo === "numero" || campo.tipo === "valuta") {
    return (
      <input type="number" inputMode="decimal" className={classe} disabled={bloccato}
             step={campo.tipo === "valuta" ? "0.01" : "any"}
             value={valore === null || valore === undefined ? "" : valore}
             onChange={(e) => onCambia(e.target.value === "" ? "" : Number(e.target.value))} />
    );
  }
  if (campo.tipo === "scelta") {
    return (
      <select className={classe} disabled={bloccato} value={valore || ""}
              onChange={(e) => onCambia(e.target.value)}>
        <option value="">—</option>
        {(campo.opzioni || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (campo.tipo === "persona") {
    return (
      <select className={classe} disabled={bloccato} value={valore || ""}
              onChange={(e) => onCambia(e.target.value)}>
        <option value="">—</option>
        {persone.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
      </select>
    );
  }
  if (campo.tipo === "mezzo") {
    return <SceltaMezzo mezzi={mezzi} valore={valore} onCambia={onCambia}
                        disabilitato={bloccato} />;
  }
  if (campo.tipo === "firma") {
    if (bloccato) {
      return valore
        ? <img src={valore} alt="firma" className="w-full h-16 object-contain border border-slate-200 rounded bg-white" />
        : <p className="text-xs text-slate-400">Non firmato.</p>;
    }
    return <CampoFirma valore={valore} onChange={onCambia} etichetta={campo.nome} />;
  }
  return <input className={classe} disabled={bloccato} value={valore || ""}
                onChange={(e) => onCambia(e.target.value)} />;
}
